/**
 * OpenAPI Specification for LoreMax AI API v1
 */

export interface OpenApiSpecOptions {
  baseUrl?: string
  workflowId?: string
}

export function generateOpenApiSpec(options: OpenApiSpecOptions = {}): string {
  const baseUrl = options.baseUrl || 'https://api.loremax.ai'

  return `openapi: 3.0.3
info:
  title: LoreMax AI API
  description: |
    API for AI-powered image and video generation using workflow pipelines.

    ## Authentication
    All endpoints require authentication via API key in the X-API-Key header.

    ## Rate Limits
    Rate limits are applied per API key. Check response headers for current limits:
    - X-RateLimit-Limit-Minute
    - X-RateLimit-Limit-Hour
    - X-RateLimit-Remaining-Minute
    - X-RateLimit-Remaining-Hour
  version: 1.0.0
  contact:
    name: LoreMax AI Support

servers:
  - url: ${baseUrl}
    description: Production server

security:
  - ApiKeyAuth: []

paths:
  /api/v1/workflow/execute:
    post:
      summary: Execute Workflow
      description: |
        Execute a workflow to generate images or videos.

        This is an asynchronous endpoint. For long-running workflows, it returns
        a generation ID and job ID that can be polled using the check endpoint.
      operationId: executeWorkflow
      tags:
        - Workflow
      requestBody:
        required: true
        content:
          application/json:
            schema:
              $ref: '#/components/schemas/ExecuteRequest'
            examples:
              textToImage:
                summary: Text to Image
                value:
                  workflowId: "Text2Image_Flux"
                  inputs:
                    prompt: "a beautiful sunset over mountains, photorealistic"
                    seed: 12345
              imageToImage:
                summary: Image to Image
                value:
                  workflowId: "Image2Image_Style"
                  inputs:
                    prompt: "convert to anime style"
                    image_url: "https://example.com/input.jpg"
      responses:
        '202':
          description: Workflow execution started
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/ExecuteResponse'
        '400':
          description: Invalid request
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/ValidationError'
        '401':
          description: Missing or invalid API key
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Error'
        '402':
          description: Insufficient tokens
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/InsufficientTokensError'
        '403':
          description: Workflow not available for this organization
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Error'
        '404':
          description: Workflow not found
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Error'
        '500':
          description: Execution failed
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Error'

  /api/v1/generations/{id}/check:
    post:
      summary: Check Generation Status
      description: |
        Check and update the status of an async generation.

        For async workflows, poll this endpoint to get the current status.
        Once completed, the result will contain the generated image/video URLs.
      operationId: checkGeneration
      tags:
        - Generations
      parameters:
        - name: id
          in: path
          required: true
          description: Generation ID returned from execute endpoint
          schema:
            type: integer
            example: 12345
      requestBody:
        required: false
        content:
          application/json:
            schema:
              $ref: '#/components/schemas/CheckRequest'
      responses:
        '200':
          description: Generation status
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/CheckResponse'
              examples:
                inProgress:
                  summary: Job in progress
                  value:
                    generationId: 12345
                    status: "checking"
                    completed: false
                    jobState: "IN_PROGRESS"
                    message: "Job is still processing"
                completed:
                  summary: Job completed
                  value:
                    generationId: 12345
                    status: "completed"
                    completed: true
                    result:
                      previewUrls:
                        - "https://cdn.loremax.ai/preview/abc123.jpg"
                      originalUrls:
                        - "https://cdn.loremax.ai/original/abc123.jpg"
                      externalUrls: []
                      contentTypes:
                        - "image"
                failed:
                  summary: Job failed
                  value:
                    generationId: 12345
                    status: "failed"
                    completed: true
                    error: "NSFW content detected"
                    errorType: "content_policy"
        '400':
          description: Invalid generation ID or missing job ID
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Error'
        '401':
          description: Missing or invalid API key
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Error'
        '403':
          description: Access denied to this generation
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Error'
        '404':
          description: Generation not found
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Error'
        '500':
          description: Check failed
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Error'

components:
  securitySchemes:
    ApiKeyAuth:
      type: apiKey
      in: header
      name: X-API-Key
      description: API key for authentication

  schemas:
    ExecuteRequest:
      type: object
      required:
        - workflowId
        - inputs
      properties:
        workflowId:
          type: string
          description: Workflow ID (visual_id or internal id)
          example: "Text2Image_Flux"
        inputs:
          type: object
          description: Workflow-specific inputs
          properties:
            prompt:
              type: string
              description: Text prompt for generation
              example: "a beautiful sunset over mountains"
            seed:
              type: integer
              description: Random seed for reproducibility (optional)
              example: 12345
            image_url:
              type: string
              format: uri
              description: Input image URL for image-to-image workflows
            video_url:
              type: string
              format: uri
              description: Input video URL for video workflows
          additionalProperties: true
        urlType:
          type: integer
          description: URL type index (0-5, default 0)
          default: 0
          minimum: 0
          maximum: 5

    ExecuteResponse:
      type: object
      properties:
        generationId:
          type: integer
          description: Unique generation ID
          example: 12345
        status:
          type: string
          description: Current status
          enum: [pending, processing, completed, failed]
          example: "pending"
        tokensCost:
          type: integer
          description: Tokens spent on this generation
          example: 10
        checkStatusUrl:
          type: string
          description: URL to check generation status
          example: "/api/v1/generations/12345"
        executionTime:
          type: integer
          description: Execution time in milliseconds
          example: 1500
        async:
          type: boolean
          description: Whether the job is async (requires polling)
          example: true
        jobId:
          type: string
          description: Async job ID (for async workflows)
          example: "job_abc123"
        previewUrls:
          type: array
          items:
            type: string
            format: uri
          description: Preview image URLs (if immediately available)
        originalUrls:
          type: array
          items:
            type: string
            format: uri
          description: Original image URLs (if immediately available)
        externalUrls:
          type: array
          items:
            type: string
            format: uri
          description: External content URLs (if immediately available)

    CheckRequest:
      type: object
      properties:
        jobId:
          type: string
          description: Job ID (optional, will be fetched from generation if not provided)
          example: "job_abc123"

    CheckResponse:
      type: object
      properties:
        generationId:
          type: integer
          description: Generation ID
          example: 12345
        status:
          type: string
          description: Current status
          enum: [checking, completed, failed]
          example: "checking"
        completed:
          type: boolean
          description: Whether the job is completed
          example: false
        jobState:
          type: string
          description: Raw job state from provider
          enum: [IN_QUEUE, IN_PROGRESS, COMPLETED, FAILED]
          example: "IN_PROGRESS"
        message:
          type: string
          description: Status message
          example: "Job is still processing"
        result:
          type: object
          description: Result data (when completed successfully)
          properties:
            previewUrls:
              type: array
              items:
                type: string
                format: uri
            originalUrls:
              type: array
              items:
                type: string
                format: uri
            externalUrls:
              type: array
              items:
                type: string
                format: uri
            contentTypes:
              type: array
              items:
                type: string
        error:
          type: string
          description: Error message (when failed)
          example: "NSFW content detected"
        errorType:
          type: string
          description: Error type code
          example: "content_policy"

    Error:
      type: object
      properties:
        error:
          type: string
          description: Error message
          example: "Workflow not found"
        details:
          type: string
          description: Additional error details

    ValidationError:
      type: object
      properties:
        error:
          type: string
          example: "Validation error"
        message:
          type: string
          example: "Missing required fields: workflowId, inputs"
        details:
          type: array
          items:
            type: object
            properties:
              field:
                type: string
              message:
                type: string
              expected:
                type: string
              received:
                type: string
        hint:
          type: object
          properties:
            expectedFormat:
              type: object
            example:
              type: object

    InsufficientTokensError:
      type: object
      properties:
        error:
          type: string
          example: "Insufficient tokens"
        details:
          type: string
        required:
          type: integer
          description: Required token amount
          example: 10
`
}

/**
 * Get OpenAPI spec as JSON object
 */
export function getOpenApiSpecAsJson(options: OpenApiSpecOptions = {}): object {
  // For now, return a simple structure.
  // In production, you might want to use a YAML parser
  const baseUrl = options.baseUrl || 'https://api.loremax.ai'

  return {
    openapi: '3.0.3',
    info: {
      title: 'LoreMax AI API',
      version: '1.0.0'
    },
    servers: [{ url: baseUrl }],
    paths: {
      '/api/v1/workflow/execute': {
        post: { summary: 'Execute Workflow' }
      },
      '/api/v1/generations/{id}/check': {
        post: { summary: 'Check Generation Status' }
      }
    }
  }
}
